import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/common/DataTable';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { feedbackTypesQueryOptions } from '@/features/feedbackTypes/api/feedbackTypes.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { feedbackListInfiniteQueryOptions } from '../api/feedback.api';
import { useFeedbackListFilters } from '../hooks/useFeedbackListFilters';
import { useMarkFeedbackRead } from '../hooks/useMarkFeedbackRead';
import { getFeedbackListFilterFields } from '../utils/getFeedbackListFilterFields';
import { getFeedbackColumns } from './FeedbackColumns';
import { FeedbackListHeader } from './FeedbackListHeader';
import { ResolveFeedbackModal } from './ResolveFeedbackModal';
import type { FeedbackListItem } from '../types/feedback.types';

export function FeedbackListTable() {
  const {
    searchText,
    filters,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    queryParams,
    handleSearchChange,
    handleFilterChange,
    applyFilterValues,
  } = useFeedbackListFilters();

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackListItem | null>(null);

  const { data: feedbackTypes } = useQuery(feedbackTypesQueryOptions());
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    feedbackListInfiniteQueryOptions(queryParams),
  );

  const markRead = useMarkFeedbackRead();

  const columns = useMemo(
    () =>
      getFeedbackColumns({
        searchText: searchText || undefined,
        actions: {
          onView: (feedback: FeedbackListItem) => {
            setSelectedFeedback(feedback);
            if (feedback.readAt === null) markRead.mutate(feedback.id);
          },
        },
      }),
    [searchText, markRead],
  );

  const filterFields = useMemo(
    () => getFeedbackListFilterFields({ feedbackTypes, filters }),
    [feedbackTypes, filters],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <FeedbackListHeader
          searchText={searchText}
          onSearchChange={handleSearchChange}
          filterOpen={filterOpen}
          onFilterToggle={() => setFilterOpen((prev) => !prev)}
          activeFilterCount={activeFilterCount}
        />

        <FilterPanelCollapse isOpen={filterOpen}>
          <AdvancedFilterPanel
            fields={filterFields}
            values={filters}
            onChange={handleFilterChange}
            onApply={applyFilterValues}
            hasPendingChanges={hasPendingChanges}
          />
        </FilterPanelCollapse>

        {loadError ? (
          <ErrorState
            error={loadError}
            fallbackTitle="Could not load feedback"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="user"
            className="lg:flex-1"
            {...loadMoreProps}
          />
        )}
      </div>

      <ResolveFeedbackModal
        feedback={selectedFeedback}
        open={selectedFeedback !== null}
        onOpenChange={(open) => !open && setSelectedFeedback(null)}
      />
    </>
  );
}
